/**
 * AddStageModal — modal for creating a new pipeline stage.
 *
 * Captures: name (required), color (optional), and — for deal pipelines —
 * win probability. Reuses ColorSelect from the canonical stage color palette
 * and the same percentage-input shape that StagesSlideout uses for editing
 * existing stages.
 *
 * Controlled component: parent owns `isOpen` / `onClose` / `onSubmit`. The
 * name input auto-focuses on open; Enter submits, Esc cancels. Empty /
 * whitespace-only names are rejected with a form-level error and prevent
 * submit. Probability is clamped 0–100 inside the modal and emitted as a
 * 0-1 decimal to match the backend's `probability` field.
 */

import {
  type FormEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { Button } from '@/lib/base/buttons/button';
import { Input, InputBase } from '@/lib/base/forms/input/input';
import { InputGroup, InputPrefix } from '@/lib/base/forms/input/input-group';
import { Dialog, Modal, ModalOverlay } from '@/lib/base/overlays/modal';
import { ColorSelect } from '@/lib/crm/utils/colors/ColorSelect';

/** Max length for the stage name. Matches PipelineColumn rename. */
const STAGE_NAME_MAX_LENGTH = 64;

/**
 * Payload emitted when the user submits the modal. Probability is a 0-1
 * decimal (e.g. 0.25 for 25%) — the same shape the parent's mutation passes
 * to the backend `update_stages` endpoint.
 */
export interface AddStageSubmitData {
  name: string;
  color: string | null;
  probability: number;
}

interface AddStageModalProps {
  /** Whether the modal is currently open. */
  isOpen: boolean;
  /**
   * Called when the modal should close (Cancel button, Esc, click outside).
   * The parent is responsible for setting `isOpen` to false.
   */
  onClose: () => void;
  /**
   * Called with the trimmed stage name + selected color + probability on
   * submit. The parent is responsible for closing the modal — typically by
   * calling `onClose` immediately so the UI feels responsive while the
   * optimistic mutation runs.
   */
  onSubmit: (data: AddStageSubmitData) => void;
  /**
   * When true, the win-probability field is rendered. Should be true for
   * deal pipelines (the only type that uses probability today).
   */
  showProbability?: boolean;
}

export function AddStageModal({
  isOpen,
  onClose,
  onSubmit,
  showProbability = false,
}: AddStageModalProps) {
  const [name, setName] = useState('');
  const [color, setColor] = useState<string | null>(null);
  // Stored as a 0-1 decimal to match the backend shape. The percent input
  // below works in 0-100 and converts on change.
  const [probability, setProbability] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Reset every field when the modal opens. Prevents stale values from
  // bleeding through if the modal is reopened after a previous submit.
  useEffect(() => {
    if (isOpen) {
      setName('');
      setColor(null);
      setProbability(0);
      setError(null);
    }
  }, [isOpen]);

  // Auto-focus the name input once the modal renders. We focus on the next
  // animation frame so the dialog's own focus management doesn't fight us.
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const id = window.requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(id);
  }, [isOpen]);

  const handleNameChange = useCallback((value: string) => {
    setName(value);
    setError(null);
  }, []);

  const handleProbabilityChange = useCallback((value: string) => {
    const parsed = parseInt(value, 10);
    if (Number.isNaN(parsed)) {
      setProbability(0);
      return;
    }
    const clamped = Math.max(0, Math.min(100, parsed));
    setProbability(clamped / 100);
  }, []);

  const handleSubmit = useCallback(
    (e?: FormEvent<HTMLFormElement>) => {
      e?.preventDefault();
      const trimmed = name.trim();
      if (!trimmed) {
        setError('Stage name is required');
        return;
      }
      onSubmit({ name: trimmed, color, probability });
    },
    [name, color, probability, onSubmit]
  );

  // Display value for the percent input. Stored as 0-1 decimal, shown 0-100.
  const probabilityPercent = Math.round(probability * 100);

  const footer = (
    <div className='flex justify-end gap-3'>
      <Button color='secondary' size='md' onClick={onClose}>
        Cancel
      </Button>
      <Button
        color='primary'
        size='md'
        type='submit'
        form='add-stage-form'
        isDisabled={name.trim().length === 0}
      >
        Add stage
      </Button>
    </div>
  );

  return (
    <ModalOverlay
      isOpen={isOpen}
      onOpenChange={open => {
        if (!open) {
          onClose();
        }
      }}
      isDismissable
    >
      <Modal className='sm:max-w-md'>
        <Dialog title='Add stage' footer={footer}>
          <form
            id='add-stage-form'
            onSubmit={handleSubmit}
            className='space-y-4'
          >
            <Input
              ref={inputRef}
              label='Stage name'
              value={name}
              onChange={handleNameChange}
              placeholder='e.g. Discovery'
              maxLength={STAGE_NAME_MAX_LENGTH}
              isInvalid={Boolean(error)}
              hint={error ?? undefined}
              autoFocus
            />

            <ColorSelect
              label='Color'
              value={color}
              onChange={setColor}
              placeholder='No color'
            />

            {showProbability && (
              <div>
                <label
                  htmlFor='add-stage-probability'
                  className='block text-sm font-medium text-text-primary mb-1.5'
                >
                  Win probability
                </label>
                <InputGroup
                  aria-label='Win probability'
                  value={String(probabilityPercent)}
                  onChange={handleProbabilityChange}
                  trailingAddon={<InputPrefix>%</InputPrefix>}
                >
                  <InputBase id='add-stage-probability' type='number' />
                </InputGroup>
              </div>
            )}
          </form>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
